import type { FunctionComponent, ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { stratify, tree } from 'd3-hierarchy';

import '@xyflow/react/dist/style.css';

import type { Node, NodeProps } from '@xyflow/react';
import { Background, Controls, Handle, MiniMap, Position, ReactFlow } from '@xyflow/react';

import { ctw } from '@/common';
import { Card, CardContent, WarningFilledSvg } from '@/components';

export type CompanyStructureNode = {
  id: string;
  data: {
    name: string;
    type: string;
    sharePercentage?: number;
  };
};

export type CompanyStructureEdge = {
  id: string;
  source: string;
  target: string;
  data: {
    sharePercentage?: number;
    role?: string;
  };
};

export type CompanyStructureData = {
  nodes: CompanyStructureNode[];
  edges: CompanyStructureEdge[];
};

export type CompanyStructureLabels = {
  title: string;
  emptyState: string;
  timeoutMessage: string;
};

export const DEFAULT_COMPANY_STRUCTURE_LABELS = {
  title: 'Corporate Structure',
  emptyState: 'Company Structure data is not available',
  timeoutMessage:
    'The request timed out either because the company was not found in the registry, or the information is currently unavailable.',
} satisfies CompanyStructureLabels;

type CompanyStructureContextValue = {
  data: CompanyStructureData;
  labels: CompanyStructureLabels;
  message?: string;
  isRequestTimedOut?: boolean;
};

const CompanyStructureContext = createContext<CompanyStructureContextValue | null>(null);

const useOptionalCompanyStructureContext = () => useContext(CompanyStructureContext);

type LayoutNode = {
  id: string;
  type: 'customNode';
  data: { label: string; sharePercentage?: string };
  position: { x: number; y: number };
};

type LayoutEdge = {
  id: string;
  source: string;
  target: string;
  label?: string;
  animated: boolean;
};

function getLayoutedElements({
  nodes,
  edges,
}: {
  nodes: Array<{ id: string; data: { label: string }; position: { x: 0; y: 0 } }>;
  edges: LayoutEdge[];
}): { nodes: LayoutNode[]; edges: LayoutEdge[] } {
  if (!nodes.length) {
    return { nodes: nodes as LayoutNode[], edges };
  }

  const hierarchy = stratify<(typeof nodes)[number]>()
    .id(node => node.id)
    .parentId(node => edges.find(edge => edge.target === node.id)?.source);
  const root = hierarchy(nodes);
  const treeLayout = tree<(typeof nodes)[number]>();
  const layout = treeLayout.nodeSize([200, 200])(root);

  return {
    nodes: layout.descendants().map(node => ({
      ...node.data,
      position: { x: node.x, y: 200 - node.y },
    })) as LayoutNode[],
    edges,
  };
}

function buildTree({
  nodes,
  edges,
}: {
  nodes: CompanyStructureNode[];
  edges: CompanyStructureEdge[];
}): { nodes: LayoutNode[]; edges: LayoutEdge[] } {
  const formattedNodes = nodes.map(node => {
    const percentage = node?.data?.sharePercentage
      ? Number(node?.data?.sharePercentage).toFixed(2)
      : undefined;

    return {
      id: node.id,
      type: 'customNode' as const,
      data: {
        label: `${node.data.name} (${node.data.type})`,
        sharePercentage: percentage
          ? `${percentage.toString().endsWith('%') ? percentage : `${percentage}%`}`
          : undefined,
      },
      position: { x: 0 as const, y: 0 as const },
    };
  });
  const formattedEdges: LayoutEdge[] = edges.map(edge => {
    const percentage = edge?.data?.sharePercentage
      ? Number(edge?.data?.sharePercentage).toFixed(2)
      : undefined;
    const role = edge?.data?.role;
    let label = '';

    if (percentage) {
      label = percentage.toString().endsWith('%') ? percentage : `${percentage}%`;
    }

    if (role) {
      label = label ? `${label} (${role})` : role;
    }

    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      ...(label ? { label } : {}),
      animated: true,
    };
  });

  return getLayoutedElements({ nodes: formattedNodes, edges: formattedEdges });
}

type StructureNodeData = Record<string, unknown> & {
  label: string;
  sharePercentage?: string;
};

type StructureNodeType = Node<StructureNodeData, 'customNode'>;

const StructureNode: FunctionComponent<NodeProps<StructureNodeType>> = ({
  positionAbsoluteX,
  positionAbsoluteY,
  data,
}) => {
  return (
    <div
      className="relative flex flex-col items-center space-y-4"
      style={{ left: positionAbsoluteX, top: positionAbsoluteY }}
    >
      <div className="relative">
        <Handle type="source" position={Position.Top} />
        <Card className="w-48">
          <CardContent className="p-4 text-center">
            <span className="notranslate" translate="no">
              {data?.label}
            </span>
            {data?.sharePercentage && (
              <div className="text-sm font-bold">{data.sharePercentage}</div>
            )}
          </CardContent>
        </Card>
        <Handle type="target" position={Position.Bottom} />
      </div>
    </div>
  );
};

const nodeTypes = { customNode: StructureNode };

const CSHeader: FunctionComponent<{
  title?: string;
  subtitle?: string;
  className?: string;
}> = ({ title: titleProp, subtitle: subtitleProp, className }) => {
  const ctx = useOptionalCompanyStructureContext();
  const labels = ctx?.labels ?? DEFAULT_COMPANY_STRUCTURE_LABELS;

  return (
    <div className={ctw('mt-4 space-y-1', className)}>
      <h3 className="mt-0 block pl-4 text-lg font-bold">{titleProp ?? labels.title}</h3>
    </div>
  );
};

const CSGraph: FunctionComponent<{
  height?: string;
  defaultViewport?: { x: number; y: number; zoom: number };
  className?: string;
}> = ({ height = '27rem', defaultViewport = { x: 500, y: 50, zoom: 0.8 }, className }) => {
  const ctx = useOptionalCompanyStructureContext();
  const data = ctx?.data;

  const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(() => {
    if (!data?.nodes?.length || !data?.edges?.length) {
      return { nodes: [], edges: [] };
    }

    return buildTree({ nodes: data.nodes, edges: data.edges });
  }, [data?.nodes, data?.edges]);

  if (!layoutedNodes.length || !layoutedEdges.length) {
    return null;
  }

  return (
    <div className={ctw('h-[27rem] p-4', className)}>
      <div className="h-full w-full rounded-sm border border-slate-200">
        <ReactFlow
          nodeTypes={nodeTypes}
          nodes={layoutedNodes}
          edges={layoutedEdges}
          defaultViewport={defaultViewport}
        >
          <MiniMap />
          <Controls />
          <Background />
        </ReactFlow>
      </div>
    </div>
  );
};

const CSEmptyState: FunctionComponent<{
  message?: string;
  className?: string;
}> = ({ message: messageProp, className }) => {
  const ctx = useOptionalCompanyStructureContext();
  const labels = ctx?.labels ?? DEFAULT_COMPANY_STRUCTURE_LABELS;

  const message =
    messageProp ??
    (ctx?.isRequestTimedOut ? labels.timeoutMessage : (ctx?.message ?? labels.emptyState));

  return (
    <div className={ctw('flex py-4 text-sm text-black/60', className)}>
      <WarningFilledSvg
        className="[&>:not(:first-child)]:stroke-background me-2 mt-px text-black/20"
        width="20"
        height="20"
      />
      <span>{message}</span>
    </div>
  );
};

type CompanyStructureComponentProps = {
  data?: CompanyStructureData;
  message?: string;
  isRequestTimedOut?: boolean;
  labels?: Partial<CompanyStructureLabels>;
  graphHeight?: string;
  defaultViewport?: { x: number; y: number; zoom: number };
  children?: ReactNode;
};

const CompanyStructureComponent: FunctionComponent<CompanyStructureComponentProps> = ({
  data: dataProp,
  message,
  isRequestTimedOut,
  labels: labelsProp,
  children,
}) => {
  const labels = useMemo(
    () => ({ ...DEFAULT_COMPANY_STRUCTURE_LABELS, ...labelsProp }),
    [labelsProp],
  );

  const data = dataProp ?? { nodes: [], edges: [] };
  const hasGraph =
    Array.isArray(data.nodes) &&
    data.nodes.length > 0 &&
    Array.isArray(data.edges) &&
    data.edges.length > 0;

  const ctxValue: CompanyStructureContextValue = {
    data,
    labels,
    message,
    isRequestTimedOut,
  };

  return (
    <CompanyStructureContext.Provider value={ctxValue}>
      <div className="space-y-2">
        {children}
        {!children && (
          <>
            <CSHeader />
            {hasGraph && <CSGraph />}
            {!hasGraph && <CSEmptyState />}
          </>
        )}
      </div>
    </CompanyStructureContext.Provider>
  );
};

const CompanyStructureWithSubComponents = Object.assign(CompanyStructureComponent, {
  Header: CSHeader,
  Graph: CSGraph,
  EmptyState: CSEmptyState,
});

export const CompanyStructure = CompanyStructureWithSubComponents;
